import { useEffect } from 'react';
import { Platform, Pressable, StyleSheet, Text } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSpring,
  withTiming,
  Easing,
} from 'react-native-reanimated';
import { theme } from '@/constants/theme';

type BubbleItemProps = {
  label: string;
  index: number;
  onSelect?: (item: string) => void;
  color?: string;
};

function BubbleItem({ label, index, onSelect, color = '#2563EB' }: BubbleItemProps) {
  const mountY = useSharedValue(12);
  const mountOpacity = useSharedValue(0);
  const pressScale = useSharedValue(1);
  const hoverBg = useSharedValue(0);

  const hex = color.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16) || 37;
  const g = parseInt(hex.slice(2, 4), 16) || 99;
  const b = parseInt(hex.slice(4, 6), 16) || 235;

  useEffect(() => {
    const delay = index * 55;
    mountOpacity.value = withDelay(
      delay,
      withTiming(1, { duration: 320, easing: Easing.out(Easing.cubic) }),
    );
    mountY.value = withDelay(
      delay,
      withSpring(0, { damping: 14, stiffness: 160 }),
    );
  }, [index]);

  const bubbleStyle = useAnimatedStyle(() => ({
    opacity: mountOpacity.value,
    transform: [
      { translateY: mountY.value },
      { scale: pressScale.value },
    ],
    ...(Platform.OS === 'web'
      ? ({
          backgroundColor: `rgba(${r},${g},${b},${0.18 + hoverBg.value * 0.22})`,
          boxShadow: hoverBg.value > 0.5
            ? `0 4px 16px rgba(${r},${g},${b},0.25), 0 0 0 1px rgba(${r},${g},${b},0.4)`
            : `0 2px 8px rgba(${r},${g},${b},0.1), 0 0 0 1px rgba(${r},${g},${b},0.25)`,
        } as any)
      : { backgroundColor: `rgba(${r},${g},${b},0.22)` }),
  }));

  return (
    <Pressable
      onPress={() => onSelect?.(label)}
      onHoverIn={() => {
        hoverBg.value = withTiming(1, { duration: 160 });
        pressScale.value = withSpring(1.04, { damping: 14, stiffness: 220 });
      }}
      onHoverOut={() => {
        hoverBg.value = withTiming(0, { duration: 240 });
        pressScale.value = withSpring(1, { damping: 14, stiffness: 220 });
      }}
      onPressIn={() => {
        pressScale.value = withSpring(0.93, { damping: 16, stiffness: 320 });
      }}
      onPressOut={() => {
        pressScale.value = withSpring(1, { damping: 12, stiffness: 240 });
      }}
    >
      <Animated.View style={[s.bubble, bubbleStyle]}>
        <Text style={[s.text, { color: `rgba(${r},${g},${b < 100 ? b + 80 : b},1)` }]}>{label}</Text>
      </Animated.View>
    </Pressable>
  );
}

export default function BubbleMenu({
  items,
  onSelect,
  color,
}: {
  items: string[];
  onSelect?: (item: string) => void;
  color?: string;
}) {
  return (
    <Animated.View style={s.row}>
      {items.map((item, i) => (
        <BubbleItem key={item} label={item} index={i} onSelect={onSelect} color={color} />
      ))}
    </Animated.View>
  );
}

const s = StyleSheet.create({
  row: { flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 10 },
  bubble: {
    borderRadius: 999,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  text: { fontFamily: theme.fonts.medium, fontSize: 13 },
});
